import { Handle, Position, type NodeProps } from "@xyflow/react";

import { formatMoney } from "../lib/format.js";

export interface GroupNodeData {
  name: string;
  color: string;
  memberIds: string[];
  total: number;
  currency: string;
  count: number;
  collapsed: boolean;
  highlight?: boolean;
  [key: string]: unknown;
}

/** A group on the canvas: a rounded backdrop behind its members when expanded,
 * or a solid summary card when collapsed. Dragging or clicking it (handled by
 * the canvas) moves or selects the whole group. */
export function GroupNode({ data }: NodeProps) {
  const { name, color, total, currency, count, collapsed, highlight } = data as GroupNodeData;

  if (collapsed) {
    return (
      <div
        className="h-full w-full rounded-xl border border-l-4 border-node-border bg-node-bg px-4 pb-4 pt-3 shadow-lg"
        style={{ borderLeftColor: color }}
      >
        <Handle type="target" position={Position.Left} className="!bg-neutral" />
        <p className="text-xs uppercase tracking-wide text-ink-muted">Group</p>
        <p className="text-sm font-semibold text-ink">{name}</p>
        <p className="figure mt-1 text-lg text-ink">{formatMoney(total, currency)}</p>
        <p className="mt-0.5 text-xs text-ink-muted">{count} items, collapsed</p>
        <Handle type="source" position={Position.Right} className="!bg-accent" />
      </div>
    );
  }

  return (
    <div
      className={`relative h-full w-full rounded-2xl border-2 ${
        highlight ? "ring-2 ring-positive" : ""
      }`}
      style={{
        borderColor: color,
        // A faint tint of the group's colour, restoring the soft backdrop.
        backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      }}
    >
      <span
        className="absolute left-3 top-2 flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium text-canvas"
        style={{ backgroundColor: color }}
      >
        {name}
        <span className="figure opacity-80">{formatMoney(total, currency)}</span>
      </span>
    </div>
  );
}
